import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React, { useState } from "react";
import { apiClient } from "@/api/axiosInstance";
import { API_ENDPOINTS, resolveEndpoint } from "@/api/endpoints";
import CustomMonacoEditor from "@/components/CustomMonacoEditor";
import { useToast } from "@/components/common/Toast";
import TagSelector from "@/components/TagSelector";
import { ApiError, handleApiError } from "@/utils/errorHandler";

interface FlowAction {
	id: string;
	name: string;
	steps: string[];
	inputSchema: Record<string, unknown>;
	outputSchema: Record<string, unknown>;
}

interface AddFlowActionProps {
	flowAction: FlowAction | null;
	flowTypeId: string;
	onClose: () => void;
}

const FLOW_ACTIONS_QUERY_KEY = ["flow-actions"] as const;

const AddFlowAction: React.FC<AddFlowActionProps> = ({
	flowAction,
	flowTypeId,
	onClose
}) => {
	const { success, error } = useToast();
	const queryClient = useQueryClient();
	const [formData, setFormData] = useState({
		name: flowAction?.name || "",
		steps: flowAction?.steps || [],
		inputSchema: flowAction?.inputSchema || {},
		outputSchema: flowAction?.outputSchema || {}
	});

	const createFlowActionMutation = useMutation({
		mutationFn: async (data: {
			name: string;
			steps: string[];
			inputSchema: Record<string, unknown>;
			outputSchema: Record<string, unknown>;
		}) => {
			const { data: response } = await apiClient.post(
				resolveEndpoint(API_ENDPOINTS, ["FLOW_ACTIONS", "CREATE"], flowTypeId),
				data
			);
			return response;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [...FLOW_ACTIONS_QUERY_KEY, flowTypeId]
			});
			success("Action created successfully");
			onClose();
		},
		onError: (error: ApiError) => {
			handleApiError(error, "Failed to create action");
		}
	});

	const updateFlowActionMutation = useMutation({
		mutationFn: async ({
			id,
			data
		}: {
			id: string;
			data: {
				name: string;
				steps: string[];
				inputSchema: Record<string, unknown>;
				outputSchema: Record<string, unknown>;
			};
		}) => {
			const { data: response } = await apiClient.put(
				resolveEndpoint(
					API_ENDPOINTS,
					["FLOW_ACTIONS", "UPDATE_BY_ID"],
					flowTypeId,
					id
				),
				data
			);
			return response;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [...FLOW_ACTIONS_QUERY_KEY, flowTypeId]
			});
			success("Action updated successfully");
			onClose();
		},
		onError: (error: ApiError) => {
			handleApiError(error, "Failed to update action");
		}
	});

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();

		if (!formData.name.trim()) {
			error("Name is required");
			return;
		}

		if (flowAction) {
			updateFlowActionMutation.mutate({
				id: flowAction.id,
				data: {
					name: formData.name.trim(),
					steps: formData.steps,
					inputSchema: formData.inputSchema,
					outputSchema: formData.outputSchema
				}
			});
		} else {
			createFlowActionMutation.mutate({
				name: formData.name.trim(),
				steps: formData.steps,
				inputSchema: formData.inputSchema,
				outputSchema: formData.outputSchema
			});
		}
	};

	const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setFormData((prev) => ({ ...prev, name: e.target.value }));
	};

	const isLoading =
		createFlowActionMutation.isPending || updateFlowActionMutation.isPending;

	return (
		<div className="space-y-6 h-full">
			<form
				onSubmit={handleSubmit}
				className="bg-white flex flex-col h-full max-w-md mx-auto rounded-lg"
			>
				<div className="mb-4 flex-1 space-y-4">
					<div>
						<label
							htmlFor="action-name"
							className="block text-sm font-medium text-gray-700"
						>
							Name
						</label>
						<input
							type="text"
							value={formData.name}
							onChange={handleNameChange}
							disabled={isLoading}
							className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
							placeholder="Enter action name"
						/>
					</div>

					<div>
						<label
							htmlFor="action-steps"
							className="block text-sm font-medium text-gray-700 mb-1"
						>
							Steps
						</label>
						<TagSelector
							value={formData.steps.map((step) => ({
								value: step,
								label: step
							}))}
							onChange={(tags) => {
								setFormData((prev) => ({
									...prev,
									steps: tags.map((tag) => String(tag.value))
								}));
							}}
							allowCustomTags={true}
							placeholder="Select steps"
							className="focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-md shadow-sm w-full"
						/>
					</div>

					<div>
						<label
							htmlFor="input-schema"
							className="block text-sm font-medium text-gray-700"
						>
							Input Schema
						</label>
						<div className="mt-1 border rounded-md overflow-hidden">
							<CustomMonacoEditor
								value={JSON.stringify(formData.inputSchema, null, 2)}
								onChange={(value) => {
									if (value) {
										setFormData((prev) => ({
											...prev,
											inputSchema: JSON.parse(value)
										}));
									}
								}}
								height="200px"
								language="json"
							/>
						</div>
					</div>

					<div>
						<label
							htmlFor="output-schema"
							className="block text-sm font-medium text-gray-700"
						>
							Output Schema
						</label>
						<div className="mt-1 border rounded-md overflow-hidden">
							<CustomMonacoEditor
								value={JSON.stringify(formData.outputSchema, null, 2)}
								onChange={(value) => {
									if (value) {
										setFormData((prev) => ({
											...prev,
											outputSchema: JSON.parse(value)
										}));
									}
								}}
								height="200px"
								language="json"
							/>
						</div>
					</div>
				</div>

				<div className="py-4 bg-white mb-[15px]">
					<button
						type="submit"
						disabled={isLoading}
						className="w-full bg-blue-fynxt text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors"
					>
						{isLoading ? (
							<>
								<FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
								{flowAction ? "Updating..." : "Creating..."}
							</>
						) : flowAction ? (
							"Update Action"
						) : (
							"Create Action"
						)}
					</button>
				</div>
			</form>
		</div>
	);
};

export default AddFlowAction;
